import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/shared/api';
import { useDebounce } from '@/shared/hooks/useDebounce';

/** Serverseitiges MinQueryLength (SearchOptions.MinQueryLength). */
export const MIN_QUERY_LENGTH = 2;

/**
 * Debounced Quick-Search gegen POST /search/quick. Der queryKey trägt den debounced Query +
 * Scope: eine später eintreffende ältere Antwort landet unter ihrem eigenen Key und
 * überschreibt nie den aktuellen (Race-/Stale-Schutz); der AbortSignal bricht Überholte ab.
 */
export function useQuickSearch(rawQuery: string, propertyIds: string[] | null, enabled: boolean) {
  const api = useApi();
  const query = rawQuery.trim();
  const debounced = useDebounce(query, 200);
  const active = enabled && debounced.length >= MIN_QUERY_LENGTH;

  const result = useQuery({
    queryKey: ['search', 'quick', debounced, propertyIds],
    queryFn: ({ signal }) => api.search.quick({ query: debounced, propertyIds }, signal),
    enabled: active,
    staleTime: 15_000,
  });

  return {
    hits: active ? (result.data?.hits ?? []) : [],
    truncated: active ? (result.data?.truncated ?? false) : false,
    isFetching: active && result.isFetching,
    isError: active && result.isError,
    /** Nutzer hat getippt, aber der Debounce/Fetch für den aktuellen Query läuft noch. */
    isPending:
      enabled && query.length >= MIN_QUERY_LENGTH && (debounced !== query || result.isFetching),
  };
}
